import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./charlie.css";
import { useAuth } from "@/contexts/AuthContext";
import { NEW_UI_HOME } from "@/components/daylight-insight/DaylightUiToggle";
import { useStore } from "@/provider/store";
import { toDisplayLabel } from "@/lib/utils/daylight-insight/format";
import { CharlieHeader } from "./components/CharlieHeader";
import { CharlieBrief } from "./components/CharlieBrief";
import { MyTasks, type TaskAction } from "./components/MyTasks";
import { PayorAcceptanceBoard } from "./components/PayorAcceptanceBoard";
import { RecordsModal } from "./components/RecordsModal";
import { MockProvider } from "./data/charlieMock";
import {
  DEFAULT_BRANCH_SYSTEM_ID,
  OPEN_BUCKETS_ON_LOAD,
  SERVICE_LABEL,
} from "@/lib/utils/charlie/constants";
import type {
  CharlieProvider,
  CharlieService,
  CharlieRecords,
  CharlieTask,
  PayorRow,
  Suggestion,
  TaskStatus,
} from "./types";

interface CharlieProps {
  /** Swap in a live provider (REST over the existing SPs) without touching a
   *  single component below. Mock data is the default for this phase. */
  provider?: CharlieProvider;
}

/**
 * Charlie — the Administrator Daily Brief.
 *
 * The feature root: it owns the data fetch and every decision the child
 * components report back, so the brief, the task list and the payor board stay
 * presentational.
 *
 * Which signals Charlie tracks — and for which role — is administered in the
 * database: the feed simply doesn't raise a task for a signal that is off, so
 * there is nothing to configure here.
 *
 * Service line comes from the shared DayLight store, not local state, so
 * Charlie and the DayLight header always agree on Home Health vs Hospice.
 * Branch is Charlie's own selection — it filters the brief client-side — but the
 * list it picks from is the store's real `accessList`, the same branches the
 * dashboards offer.
 */
export default function Charlie({ provider = MockProvider }: CharlieProps) {
  // Branches come from the shared DayLight store — the same real list the
  // dashboards' BranchScope offers, already scoped by the config API to what
  // this user may see and to the selected service line.
  const { service, setService, accessList: branches, branchOptions, configLoading } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();

  // "" until the config lands and the default branch can be resolved. The feed
  // waits for it rather than firing once for "all" and again for the default.
  const [branch, setBranch] = useState("");
  const [sugs, setSugs] = useState<Suggestion[]>([]);
  const [tasks, setTasks] = useState<CharlieTask[]>([]);
  const [payor, setPayor] = useState<PayorRow[]>([]);
  const [openBuckets, setOpenBuckets] = useState<Record<string, boolean>>({});
  const [tkView, setTkView] = useState<TaskStatus>("open");
  const [modal, setModal] = useState<CharlieRecords | null>(null);
  // The suggestion whose drill is in flight — its button reads "Opening…" while
  // the records are fetched.
  const [drillBusy, setDrillBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // The service line the branch was last resolved for. A ref, not state: it only
  // ever guards the effect below, so it must not itself cause a render.
  const resolvedFor = useRef<CharlieService | null>(null);

  // Charlie opens on one branch rather than on "all" — see
  // DEFAULT_BRANCH_SYSTEM_ID for why, and for when to drop this. Resolved by
  // SystemID because the dropdown works in labels.
  //
  // Once per SERVICE LINE, not once per mount: switching lines swaps the branch
  // list out from under the selection, so the default has to resolve again for
  // the new list. Resolving only while `branch` was empty left Hospice → Home
  // Health stuck on whatever the switch had fallen back to.
  useEffect(() => {
    if (configLoading) return;
    if (resolvedFor.current !== service) {
      resolvedFor.current = service;
      setBranch(branchOptions.find((b) => b.value === DEFAULT_BRANCH_SYSTEM_ID)?.label ?? "all");
      return;
    }
    // Within a service line the selection belongs to the user — picking "All my
    // branches" sticks — but the list can still change underneath it (a config
    // refetch), and a branch that vanished would ask the SP for one this user
    // can't see, so the scope falls back to all branches.
    if (branch && branch !== "all" && !branches.includes(branch)) setBranch("all");
  }, [service, configLoading, branchOptions, branches, branch]);

  // Clearing the branch in the same update as the service line keeps the feed
  // from firing once for the new line with the old line's branch and again for
  // the resolved default — the fetch below waits for a resolved branch.
  const changeService = useCallback(
    (s: CharlieService) => {
      setService(s);
      setBranch("");
    },
    [setService],
  );

  useEffect(() => {
    // Nothing to ask for until the default branch is resolved.
    if (!branch) return;
    let live = true;
    setLoading(true);
    setErr(null);
    Promise.all([provider.feed({ svc: service, branch }), provider.payorBoard({ svc: service })])
      .then(([f, p]) => {
        if (!live) return;
        setSugs(f.suggestions.map((x) => ({ ...x, status: "open" as const })));
        setTasks(f.tasks);
        setPayor(p);
        // Open the first few buckets so the brief reads as a list of decisions
        // rather than a wall of closed headers. The categories come from the
        // data, so which ones those are is only known once it arrives.
        const ob: Record<string, boolean> = {};
        Array.from(new Set(f.suggestions.map((x) => x.bucket))).forEach(
          (b, i) => (ob[b] = i < OPEN_BUCKETS_ON_LOAD),
        );
        setOpenBuckets(ob);
      })
      .catch((e) => live && setErr(String(e)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // Branch is an SP parameter (`@BranchSystemID`), so it refetches alongside
    // the service line rather than filtering what's already on screen.
  }, [provider, service, branch]);

  // The feed is already scoped to the service line and branch; this only keeps
  // the mock fallback (which serves both service lines at once) honest.
  const visible = useMemo(() => sugs.filter((s) => s.svc === service), [sugs, service]);
  const needCount = visible.filter((s) => s.status === "open").length;

  const visTasks = tasks.filter((t) => t.svc === service && t.status === tkView);

  // Charlie greets by first name; the session carries the full one.
  const userName = (user?.name || user?.full_name || "").split(" ")[0] || "there";

  /**
   * Runs one write, and applies its screen change only if the database took it.
   *
   * Every decision below is a real write now, so an optimistic update would let
   * the user walk away believing a task was accepted that never was. On a
   * failure nothing moves and the error bar says so.
   */
  async function commit(write: () => Promise<void>) {
    setErr(null);
    try {
      await write();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function acceptSave(sug: Suggestion, notes: string, due: string) {
    await commit(async () => {
      const task = await provider.acceptSuggestion(sug, { notes, due });
      setTasks((t) => [task, ...t]);
      // The accepted item stays in the brief, greyed — the list must not
      // reshuffle under the user mid-review. It leaves on the next refetch, by
      // which time the SP has moved it into the assigned list.
      setSugs((all) => all.map((s) => (s.id === sug.id ? { ...s, status: "accepted" } : s)));
    });
  }

  async function rejectSug(sug: Suggestion) {
    // The reason is the whole point of a reject — it is what tunes the rule, so
    // there is no path to reject without one. The SP rejects a blank one too.
    const reason = window.prompt("Reject — reason (required). Charlie keeps this to tune the rule:");
    if (!reason?.trim()) return;
    await commit(async () => {
      await provider.rejectSuggestion(sug.id, reason.trim());
      setSugs((all) =>
        all.map((s) => (s.id === sug.id ? { ...s, status: "rejected", reason: reason.trim() } : s)),
      );
    });
  }

  async function taskAction(id: string, action: TaskAction) {
    if (action === "complete") {
      const t = tasks.find((x) => x.id === id);
      const status: TaskStatus = t?.status === "complete" ? "open" : "complete";
      await commit(async () => {
        await provider.updateTask(id, { status });
        setTasks((all) => all.map((x) => (x.id === id ? { ...x, status } : x)));
      });
    }
    if (action === "resched") {
      const d = window.prompt("New due date (YYYY-MM-DD):");
      if (!d) return;
      await commit(async () => {
        await provider.updateTask(id, { due: d });
        setTasks((all) => all.map((x) => (x.id === id ? { ...x, due: d } : x)));
      });
    }
    if (action === "cancel") {
      const r = window.prompt("Cancel — reason (required):");
      if (!r?.trim()) return;
      await commit(async () => {
        await provider.updateTask(id, { status: "cancelled", cancelReason: r.trim() });
        setTasks((all) =>
          all.map((x) => (x.id === id ? { ...x, status: "cancelled", cancelReason: r.trim() } : x)),
        );
      });
    }
  }

  /**
   * Open the records behind one suggestion.
   *
   * The rows are fetched on the click rather than carried by the feed: most
   * tasks are never drilled into, and each `SignalDrillDownType` returns its
   * own columns, so there is nothing to fold into the feed's shape.
   */
  async function openRecords(sug: Suggestion) {
    setDrillBusy(sug.id);
    setErr(null);
    try {
      setModal(await provider.drillDown(sug));
    } catch (e) {
      // No modal on a failure: an empty table would read as "no records",
      // which is a different thing from "Charlie couldn't ask".
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDrillBusy(null);
    }
  }

  async function cyclePolicy(row: PayorRow) {
    const order = ["Accept", "Review", "Decline"] as const;
    const next = order[(order.indexOf(row.policy) + 1) % order.length];
    await commit(async () => {
      await provider.savePayorPolicy(service, row.payor, next);
      setPayor((p) => p.map((r) => (r.payor === row.payor ? { ...r, policy: next } : r)));
    });
  }

  return (
    <div className="ch-root">
      <CharlieHeader
        service={service}
        setService={changeService}
        branch={branch}
        setBranch={setBranch}
        branches={branches}
        onBrandClick={() => navigate(NEW_UI_HOME)}
      />

      <div className="wrap">
        <div className="hello">
          <h1>Good morning, {userName}</h1>
          <div className="sub">
            {/* The branch clause waits for the config — an unresolved branch
                would leave a dangling separator on first paint. */}
            <b>Charlie</b> reviewed {SERVICE_LABEL[service]}
            {branch ? ` · ${branch === "all" ? "all branches" : toDisplayLabel(branch)}` : ""} this
            morning — <b>{needCount}</b> items need your decision. Accept to make it your task; reject and Charlie
            learns.
          </div>
        </div>

        {err && (
          <div className="errbar">
            {err} — nothing was changed. Showing what the database actually holds rather than
            something wrong.
          </div>
        )}

        {loading ? (
          <div className="loading">Charlie is reviewing {SERVICE_LABEL[service]}…</div>
        ) : (
          <div className="grid">
            <CharlieBrief
              suggestions={visible}
              openBuckets={openBuckets}
              toggleBucket={(b) => setOpenBuckets((o) => ({ ...o, [b]: !o[b] }))}
              onAccept={acceptSave}
              onReject={rejectSug}
              onRecords={openRecords}
              busyId={drillBusy}
            />

            <aside>
              <MyTasks tasks={visTasks} view={tkView} setView={setTkView} onAction={taskAction} />
              <PayorAcceptanceBoard rows={payor} onCyclePolicy={cyclePolicy} />
            </aside>
          </div>
        )}
      </div>

      {modal && <RecordsModal records={modal} onClose={() => setModal(null)} />}
    </div>
  );
}
